import { useCallback, useEffect, useRef, useState } from 'react';

const useWebSocket = (url, onMessage, getToken, shouldConnect = true) => {
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const isMountedRef = useRef(false);
  const [isConnected, setIsConnected] = useState(false);

  const latestOnMessageRef = useRef(onMessage);
  const latestGetTokenRef = useRef(getToken);
  const latestShouldConnectRef = useRef(shouldConnect);

  const MAX_RETRIES = 10;

  // Keep latest callback references
  useEffect(() => {
    latestOnMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    latestGetTokenRef.current = getToken;
  }, [getToken]);

  useEffect(() => {
    latestShouldConnectRef.current = shouldConnect;
  }, [shouldConnect]);

  const disconnect = useCallback(() => {
    console.log('🔌 Disconnecting WebSocket');
    
    // Clear any pending reconnection attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Close the connection cleanly
    if (wsRef.current) {
      wsRef.current.close(1000, 'Intentional disconnect');
      wsRef.current = null;
    }
    
    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  const connect = useCallback(() => {
    // Don't connect if we shouldn't or if already connected
    if (!latestShouldConnectRef.current || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Check if we have a token before connecting
    const token = latestGetTokenRef.current?.();
    if (!token) {
      console.log('🔌 No token available, skipping WebSocket connection');
      return;
    }

    console.log('🔌 Attempting WebSocket connection to:', url);
    const socket = new WebSocket(url);
    wsRef.current = socket;

    socket.onopen = () => {
      console.log('✅ WebSocket connected');
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;

      // Send authentication immediately
      if (token) {
        socket.send(JSON.stringify({ type: 'auth', token }));
      }
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'auth-ok') {
          console.log('✅ WebSocket authenticated');
        } else if (data.type === 'auth-error') {
          console.error('❌ WebSocket authentication failed:', data.message);
          socket.close(1008, 'Authentication failed');
        } else {
          latestOnMessageRef.current?.(data);
        }
      } catch {
        latestOnMessageRef.current?.(event.data); // fallback for non-JSON
      }
    };

    socket.onclose = (event) => {
      console.log('❌ WebSocket closed', event.code, event.reason);
      setIsConnected(false);
      wsRef.current = null;

      // Clear any existing reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Don't reconnect if:
      // - Component is unmounted
      // - Clean close (code 1000)
      // - Authentication failed (code 1008)
      // - We shouldn't be connecting
      if (!isMountedRef.current || 
          event.code === 1000 || 
          event.code === 1008 || 
          !latestShouldConnectRef.current) {
        return;
      }

      // Don't reconnect if no token available
      const currentToken = latestGetTokenRef.current?.();
      if (!currentToken) {
        console.log('🔌 No token available for reconnection');
        return;
      }

      if (reconnectAttemptsRef.current >= MAX_RETRIES) {
        console.warn('🚫 Max WebSocket reconnect attempts reached.');
        return;
      }

      const delay = Math.min(30000, Math.pow(2, reconnectAttemptsRef.current) * 1000);
      console.log(`⏳ Reconnecting WebSocket in ${delay / 1000}s... (attempt ${reconnectAttemptsRef.current + 1}/${MAX_RETRIES})`);
      
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectAttemptsRef.current++;
        connect();
      }, delay);
    };

    socket.onerror = (event) => {
      console.error('🚨 WebSocket error', event);
      setIsConnected(false);
    };
  }, [url]);

  // Connect/disconnect based on shouldConnect flag
  useEffect(() => {
    if (shouldConnect) {
      connect();
    } else {
      disconnect();
    }
  }, [shouldConnect, connect, disconnect]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      console.log('🔌 Cleaning up WebSocket connection');
      isMountedRef.current = false;
      disconnect();
    };
  }, [disconnect]);

  return {
    send: (data) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(typeof data === 'string' ? data : JSON.stringify(data));
        return true;
      } else {
        console.warn('⚠️ Tried to send on closed WebSocket', wsRef.current?.readyState);
        return false;
      }
    },
    isConnected,
    connect,
    disconnect,
    reconnectAttempts: reconnectAttemptsRef.current
  };
};

export default useWebSocket;